import React, { useState, useEffect } from 'react'
import { 
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CameraIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../../contextos/autenticacion/ContextoAutenticacion'
import AlertaPersonalizada from '../../../componentes/interfaz-usuario/notificaciones/AlertaPersonalizada'
import { useAlertas } from '../../../ganchos/useAlertas'
import api from '../../../services/authService'

interface PerfilUsuario {
  id: string
  nombre: string
  apellido: string
  email: string
  telefono: string
  direccion: string
  ciudad: string
  pais: string
  empresa: string
  cargo: string
  fechaRegistro: string
  avatar?: string
}

const PaginaPerfil: React.FC = () => {
  const { isAuthenticated } = useAuth()
  const { alerta, mostrarConfirmacion, mostrarExito, mostrarError, cerrarAlerta } = useAlertas()
  const [perfil, setPerfil] = useState<PerfilUsuario | null>(null)
  const [editando, setEditando] = useState(false)
  const [perfilEditado, setPerfilEditado] = useState<PerfilUsuario | null>(null)
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      cargarPerfil()
    }
  }, [isAuthenticated])

  const cargarPerfil = async () => {
    try {
      if (!isAuthenticated) {
        console.error('No se encontró sesión de autenticación válida')
        return
      }

      const response = await api.get('/auth/me')
      const userData = response.data
      
      const perfilUsuario: PerfilUsuario = {
        id: userData.id_usuario?.toString() || '',
        nombre: userData.nombre || '',
        apellido: userData.apellido || '',
        email: userData.correo || '',
        telefono: userData.telefono || '',
        direccion: userData.direccion || '',
        ciudad: userData.ciudad || '',
        pais: userData.pais || '',
        empresa: userData.empresa || '',
        cargo: userData.cargo || '',
        fechaRegistro: userData.ultimo_ingreso || '',
        avatar: userData.avatar || ''
      }
      
      setPerfil(perfilUsuario)
      setPerfilEditado(perfilUsuario)
    } catch (error) {
      console.error('Error al cargar perfil:', error)
      // En caso de error, usar datos de ejemplo como fallback
      const perfilEjemplo: PerfilUsuario = {
        id: '1',
        nombre: 'Usuario',
        apellido: 'Demo',
        email: 'usuario@logixport.com',
        telefono: '',
        direccion: '',
        ciudad: '',
        pais: '',
        empresa: '',
        cargo: '',
        fechaRegistro: new Date().toISOString()
      }
      setPerfil(perfilEjemplo)
      setPerfilEditado(perfilEjemplo)
    }
  }

  const handleInputChange = (campo: keyof PerfilUsuario, valor: string) => {
    if (perfilEditado) {
      setPerfilEditado({
        ...perfilEditado,
        [campo]: valor
      })
    }
  }

  const handleGuardar = async () => {
    if (!isAuthenticated) {
      mostrarError(
        'Sesión no válida',
        'Su sesión ha expirado por motivos de seguridad. Por favor, inicie sesión nuevamente para continuar.'
      )
      return
    }

    if (!perfilEditado) {
      mostrarError(
        'Error de validación',
        'No se han detectado datos válidos para procesar. Por favor, verifique la información e intente nuevamente.'
      )
      return
    }

    if (!perfilEditado.nombre || !perfilEditado.apellido) {
      mostrarError(
        'Información requerida',
        'Los campos Nombre y Apellido son obligatorios para completar su perfil. Por favor, proporcione esta información.'
      )
      return
    }

    // Mostrar alerta de confirmación con los datos que se van a actualizar
    const mensajeConfirmacion = 
      'Se procederá a actualizar la siguiente información de su perfil:\n\n' +
      `• Nombre completo: ${perfilEditado.nombre} ${perfilEditado.apellido}\n` +
      `• Teléfono de contacto: ${perfilEditado.telefono || 'No especificado'}\n` +
      `• Dirección: ${perfilEditado.direccion || 'No especificada'}\n` +
      `• Ciudad: ${perfilEditado.ciudad || 'No especificada'}\n` +
      `• País: ${perfilEditado.pais || 'No especificado'}\n` +
      `• Empresa: ${perfilEditado.empresa || 'No especificada'}\n` +
      `• Cargo: ${perfilEditado.cargo || 'No especificado'}\n\n` +
      '¿Confirma que desea proceder con estos cambios?'

    mostrarConfirmacion(
      {
        titulo: 'Confirmación de actualización',
        mensaje: mensajeConfirmacion,
        textoConfirmar: 'Confirmar actualización',
        textoCancelar: 'Revisar información'
      },
      async () => {
        setGuardando(true)
        
        try {
          // Preparar los datos para enviar
          const datosActualizacion = {
            nombre: perfilEditado.nombre,
            apellido: perfilEditado.apellido,
            telefono: perfilEditado.telefono,
            direccion: perfilEditado.direccion,
            ciudad: perfilEditado.ciudad,
            pais: perfilEditado.pais,
            empresa: perfilEditado.empresa,
            cargo: perfilEditado.cargo
          }

          const response = await api.put('/auth/profile', datosActualizacion)
          const perfilActualizado = response.data
          
          // Actualizar el estado con los datos devueltos por la API
          const perfilUsuario: PerfilUsuario = {
            id: perfilActualizado.id_usuario?.toString() || perfilEditado.id,
            nombre: perfilActualizado.nombre || '',
            apellido: perfilActualizado.apellido || '',
            email: perfilActualizado.correo || perfilEditado.email,
            telefono: perfilActualizado.telefono || '',
            direccion: perfilActualizado.direccion || '',
            ciudad: perfilActualizado.ciudad || '',
            pais: perfilActualizado.pais || '',
            empresa: perfilActualizado.empresa || '',
            cargo: perfilActualizado.cargo || '',
            fechaRegistro: perfilEditado.fechaRegistro,
            avatar: perfilActualizado.avatar || perfilEditado.avatar
          }
          
          setPerfil(perfilUsuario)
          setPerfilEditado(perfilUsuario)
          setEditando(false)
          
          // Mostrar alerta de éxito
          const mensajeExito = 
            'Su información de perfil ha sido actualizada satisfactoriamente:\n\n' +
            `• Nombre completo: ${perfilUsuario.nombre} ${perfilUsuario.apellido}\n` +
            `• Teléfono: ${perfilUsuario.telefono || 'No especificado'}\n` +
            `• Dirección: ${perfilUsuario.direccion || 'No especificada'}\n` +
            `• Ciudad: ${perfilUsuario.ciudad || 'No especificada'}\n` +
            `• País: ${perfilUsuario.pais || 'No especificado'}\n` +
            `• Empresa: ${perfilUsuario.empresa || 'No especificada'}\n` +
            `• Cargo: ${perfilUsuario.cargo || 'No especificado'}\n\n` +
            'Los cambios han sido guardados y están disponibles inmediatamente en su cuenta.'

          mostrarExito('Perfil actualizado correctamente', mensajeExito)
          
        } catch (error) {
          console.error('Error al guardar perfil:', error)
          
          const mensajeError = 
            'No ha sido posible procesar la actualización de su perfil. Las posibles causas incluyen:\n\n' +
            '• Problemas de conectividad con el servidor\n' +
            '• Sesión de usuario expirada\n' +
            '• Formato de datos no válido\n' +
            '• Error temporal del sistema\n\n' +
            'Le recomendamos verificar su conexión a internet e intentar nuevamente.\n' +
            'Si el inconveniente persiste, por favor contacte al equipo de soporte técnico.'

          mostrarError('Error en la actualización', mensajeError)
        } finally {
          setGuardando(false)
        }
      }
    )
  }

  const handleCancelar = () => {
    // Verificar si hay cambios sin guardar
    const hayCambios = perfil && perfilEditado && (
      perfil.nombre !== perfilEditado.nombre ||
      perfil.apellido !== perfilEditado.apellido ||
      perfil.telefono !== perfilEditado.telefono ||
      perfil.direccion !== perfilEditado.direccion ||
      perfil.ciudad !== perfilEditado.ciudad ||
      perfil.pais !== perfilEditado.pais ||
      perfil.empresa !== perfilEditado.empresa ||
      perfil.cargo !== perfilEditado.cargo
    )

    if (hayCambios) {
      const cambiosDetectados = []
      if (perfilEditado?.nombre !== perfil?.nombre) cambiosDetectados.push(`• Nombre: "${perfilEditado?.nombre}"`)
      if (perfilEditado?.apellido !== perfil?.apellido) cambiosDetectados.push(`• Apellido: "${perfilEditado?.apellido}"`)
      if (perfilEditado?.telefono !== perfil?.telefono) cambiosDetectados.push(`• Teléfono: "${perfilEditado?.telefono}"`)
      if (perfilEditado?.direccion !== perfil?.direccion) cambiosDetectados.push(`• Dirección: "${perfilEditado?.direccion}"`)
      if (perfilEditado?.ciudad !== perfil?.ciudad) cambiosDetectados.push(`• Ciudad: "${perfilEditado?.ciudad}"`)
      if (perfilEditado?.pais !== perfil?.pais) cambiosDetectados.push(`• País: "${perfilEditado?.pais}"`)
      if (perfilEditado?.empresa !== perfil?.empresa) cambiosDetectados.push(`• Empresa: "${perfilEditado?.empresa}"`)
      if (perfilEditado?.cargo !== perfil?.cargo) cambiosDetectados.push(`• Cargo: "${perfilEditado?.cargo}"`)

      const mensajeAdvertencia = 
        'Ha realizado modificaciones que no han sido guardadas.\n\n' +
        'Los siguientes cambios se perderán si continúa:\n\n' +
        cambiosDetectados.join('\n') + '\n\n' +
        '¿Está seguro de que desea descartar estos cambios?'

      mostrarConfirmacion(
        {
          titulo: 'Cambios sin guardar detectados',
          mensaje: mensajeAdvertencia,
          textoConfirmar: 'Descartar cambios',
          textoCancelar: 'Continuar editando'
        },
        () => {
          setPerfilEditado(perfil)
          setEditando(false)
        }
      )
    } else {
      setPerfilEditado(perfil)
      setEditando(false)
    }
  }

  const handleCambiarAvatar = () => {
    // Aquí iría la lógica para cambiar el avatar
    console.log('Cambiar avatar')
  }

  if (!perfil || !perfilEditado) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-300 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            <div className="h-20 bg-gray-300 rounded"></div>
            <div className="h-40 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-zinc-100">Mi Perfil</h1>
            <p className="text-slate-600 dark:text-zinc-400">Gestiona tu información personal y configuración de cuenta</p>
          </div>
          {!editando ? (
            <button
              onClick={() => setEditando(true)}
              className="bg-sky-600 hover:bg-sky-500 text-white px-5 py-2.5 rounded-xl text-sm font-bold tracking-wide flex items-center space-x-2 transition-colors shadow-sm"
            >
              <PencilIcon className="h-4 w-4" />
              <span>Editar Perfil</span>
            </button>
          ) : (
            <div className="flex space-x-3">
              <button
                onClick={handleGuardar}
                disabled={guardando}
                className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-400 text-white px-5 py-2.5 rounded-xl text-sm font-bold tracking-wide flex items-center space-x-2 transition-colors shadow-sm"
              >
                <CheckIcon className="h-4 w-4" />
                <span>{guardando ? 'Guardando...' : 'Guardar'}</span>
              </button>
              <button
                onClick={handleCancelar}
                disabled={guardando}
                className="bg-slate-800 hover:bg-slate-700 disabled:bg-slate-400 text-white dark:bg-zinc-800 dark:hover:bg-zinc-700 px-5 py-2.5 rounded-xl text-sm font-bold tracking-wide flex items-center space-x-2 transition-colors shadow-sm"
              >
                <XMarkIcon className="h-4 w-4" />
                <span>Cancelar</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información del avatar */}
        <div className="lg:col-span-1">
          <div className="rounded-[24px] border border-slate-200/60 bg-white/70 p-6 shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000] dark:bg-none">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-32 h-32 bg-slate-200 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  {perfil.avatar ? (
                    <img
                      src={perfil.avatar}
                      alt="Avatar"
                      className="w-32 h-32 rounded-full object-cover"
                    />
                  ) : (
                    <UserIcon className="h-16 w-16 text-slate-500 dark:text-zinc-400" />
                  )}
                </div>
                {editando && (
                  <button
                    onClick={handleCambiarAvatar}
                    className="absolute bottom-4 right-0 bg-sky-600 hover:bg-sky-500 text-white p-2 rounded-full shadow-lg transition-colors"
                  >
                    <CameraIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              <h2 className="text-xl font-bold text-slate-900 dark:text-zinc-100">
                {perfil.nombre} {perfil.apellido}
              </h2>
              <p className="text-slate-600 dark:text-zinc-400">{perfil.cargo}</p>
              <p className="text-sm text-slate-500 dark:text-zinc-500">{perfil.empresa}</p>
              
              <div className="mt-4 pt-4 border-t border-slate-200/60 dark:border-zinc-800/50">
                <p className="text-sm text-slate-500 dark:text-zinc-400">
                  Miembro desde {new Date(perfil.fechaRegistro).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Información personal */}
        <div className="lg:col-span-2">
          <div className="rounded-[24px] border border-slate-200/60 bg-white/70 p-6 shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000] dark:bg-none">
            <h3 className="text-lg font-medium text-slate-900 dark:text-zinc-100 mb-6">
              Información Personal
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nombre */}
              <div>
                <label htmlFor="perfil-nombre" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Nombre
                </label>
                {editando ? (
                  <input
                    id="perfil-nombre"
                    name="perfil.nombre"
                    type="text"
                    value={perfilEditado.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                    <span className="text-slate-900 dark:text-zinc-100">{perfil.nombre}</span>
                  </div>
                )}
              </div>

              {/* Apellido */}
              <div>
                <label htmlFor="perfil-apellido" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Apellido
                </label>
                {editando ? (
                  <input
                    id="perfil-apellido"
                    name="perfil.apellido"
                    type="text"
                    value={perfilEditado.apellido}
                    onChange={(e) => handleInputChange('apellido', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <UserIcon className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                    <span className="text-slate-900 dark:text-zinc-100">{perfil.apellido}</span>
                  </div>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="perfil-email" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Correo Electrónico
                </label>
                {editando ? (
                  <input
                    id="perfil-email"
                    name="perfil.email"
                    type="email"
                    value={perfilEditado.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <EnvelopeIcon className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                    <span className="text-slate-900 dark:text-zinc-100">{perfil.email}</span>
                  </div>
                )}
              </div>

              {/* Teléfono */}
              <div>
                <label htmlFor="perfil-telefono" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Teléfono
                </label>
                {editando ? (
                  <input
                    id="perfil-telefono"
                    name="perfil.telefono"
                    type="tel"
                    value={perfilEditado.telefono}
                    onChange={(e) => handleInputChange('telefono', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <PhoneIcon className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                    <span className="text-slate-900 dark:text-zinc-100">{perfil.telefono}</span>
                  </div>
                )}
              </div>

              {/* Dirección */}
              <div className="md:col-span-2">
                <label htmlFor="perfil-direccion" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Dirección
                </label>
                {editando ? (
                  <input
                    id="perfil-direccion"
                    name="perfil.direccion"
                    type="text"
                    value={perfilEditado.direccion}
                    onChange={(e) => handleInputChange('direccion', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <MapPinIcon className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                    <span className="text-slate-900 dark:text-zinc-100">{perfil.direccion}</span>
                  </div>
                )}
              </div>

              {/* Ciudad */}
              <div>
                <label htmlFor="perfil-ciudad" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Ciudad
                </label>
                {editando ? (
                  <input
                    id="perfil-ciudad"
                    name="perfil.ciudad"
                    type="text"
                    value={perfilEditado.ciudad}
                    onChange={(e) => handleInputChange('ciudad', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <MapPinIcon className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                    <span className="text-slate-900 dark:text-zinc-100">{perfil.ciudad}</span>
                  </div>
                )}
              </div>

              {/* País */}
              <div>
                <label htmlFor="perfil-pais" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  País
                </label>
                {editando ? (
                  <select
                    id="perfil-pais"
                    name="perfil.pais"
                    value={perfilEditado.pais}
                    onChange={(e) => handleInputChange('pais', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  >
                    <option value="Colombia">Colombia</option>
                    <option value="México">México</option>
                    <option value="Argentina">Argentina</option>
                    <option value="Chile">Chile</option>
                    <option value="Perú">Perú</option>
                  </select>
                ) : (
                  <div className="flex items-center space-x-2">
                    <MapPinIcon className="h-5 w-5 text-slate-400 dark:text-zinc-500" />
                    <span className="text-slate-900 dark:text-zinc-100">{perfil.pais}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Información profesional */}
          <div className="rounded-[24px] border border-slate-200/60 bg-white/70 p-6 shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000] dark:bg-none mt-6">
            <h3 className="text-lg font-medium text-slate-900 dark:text-zinc-100 mb-6">
              Información Profesional
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Empresa */}
              <div>
                <label htmlFor="perfil-empresa" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Empresa
                </label>
                {editando ? (
                  <input
                    id="perfil-empresa"
                    name="perfil.empresa"
                    type="text"
                    value={perfilEditado.empresa}
                    onChange={(e) => handleInputChange('empresa', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  />
                ) : (
                  <span className="text-slate-900 dark:text-zinc-100">{perfil.empresa}</span>
                )}
              </div>

              {/* Cargo */}
              <div>
                <label htmlFor="perfil-cargo" className="block text-sm font-medium text-slate-700 dark:text-zinc-300 mb-2">
                  Cargo
                </label>
                {editando ? (
                  <input
                    id="perfil-cargo"
                    name="perfil.cargo"
                    type="text"
                    value={perfilEditado.cargo}
                    onChange={(e) => handleInputChange('cargo', e.target.value)}
                    className="block w-full rounded-xl border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-900 dark:text-zinc-100 shadow-sm focus:border-sky-500 focus:ring-sky-500/20 focus:ring-2 outline-none transition-all duration-200"
                  />
                ) : (
                  <span className="text-slate-900 dark:text-zinc-100">{perfil.cargo}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Componente de Alertas Personalizadas */}
      {alerta && (
        <AlertaPersonalizada
          tipo={alerta.tipo}
          titulo={alerta.titulo}
          mensaje={alerta.mensaje}
          mostrar={alerta.mostrar}
          onConfirmar={alerta.onConfirmar}
          onCancelar={alerta.onCancelar}
          onCerrar={cerrarAlerta}
          textoConfirmar={alerta.textoConfirmar}
          textoCancelar={alerta.textoCancelar}
          autoClose={alerta.autoClose}
          duracion={alerta.duracion}
        />
      )}
    </div>
  )
}

export default PaginaPerfil
